import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { keyName, keyText, keyType, lineKeyCount, lineKeySlots, type KeySlot, type PhoneKey, type PhoneModel } from '@stitch/pbx-adapter/phones';

/** The light on a key, by what it does (as the phone lights it). */
function ledOf(k: PhoneKey | undefined): 'green' | 'amber' | 'red' | '' {
  switch (k?.type) {
    case 'line':
    case 'person':
    case 'queue':
    case 'ringGroup':
    case 'intercom':
      return 'green';
    case 'park':
    case 'voicemail':
      return 'amber';
    case 'timeRule':
    case 'dnd':
    case 'forward':
      return 'red';
    default:
      return '';
  }
}

export interface PhoneLayoutProps {
  model: PhoneModel;
  keys: readonly PhoneKey[];
  /** Name for a number (people, queues, park orbits...), to label keys the way the phone will. */
  nameOf: (n: string) => string | undefined;
  /** The key being edited. */
  selected?: string;
  /** Click a key: edit it (admins), or see what it does (everyone). */
  onSelect?: (key: string) => void;
  /** Expansion modules drawn beside the phone. */
  modules?: number;
  /** Shown on the screen's top line: "201 Dana Ruiz". */
  owner?: string;
  /** Mark keys left to the phone's default, so empty keys are easy to find while editing. */
  editing?: boolean;
  /** Keys changed but not saved yet. */
  changed?: ReadonlySet<string>;
  /**
   * How many line keys the phone really has (lineKeyCount: the phone system's list when known,
   * and never fewer than the highest key set). Default: the model's. Every one is on a page.
   */
  lineKeys?: number;
}

/**
 * A desk phone as it looks, with its programmable keys where they really are (left and right of
 * the screen, or on a touch screen), page by page, plus expansion modules. Each key is a button
 * labelled the way the phone will label it.
 */
export function PhoneLayout({ model, keys, nameOf, selected, onSelect, modules = 0, owner, editing, changed, lineKeys }: PhoneLayoutProps) {
  const count = lineKeys ?? lineKeyCount(model, keys);
  const { pages, pageKey } = useMemo(() => lineKeySlots(model, count), [model, count]);
  const byKey = useMemo(() => new Map(keys.map((k) => [k.key, k])), [keys]);
  const [page, setPage] = useState(0);
  const [expPage, setExpPage] = useState(0);
  // Follow the selected key to its page (picking "Key 12" from a list shows page 2).
  useEffect(() => {
    if (!selected) return;
    const p = pages.findIndex((slots) => slots.some((s) => s.key === selected));
    if (p >= 0) setPage(p);
    const e = /^E\d-(\d+)$/.exec(selected);
    if (e && model.expansion) setExpPage(Math.floor((Number(e[1]) - 1) / model.expansion.perPage));
  }, [selected, pages, model.expansion]);

  // Fewer pages than before (another phone, or a smaller list): back to the first.
  useEffect(() => {
    if (page >= pages.length) setPage(0);
  }, [page, pages.length]);
  const slots = pages[page] ?? [];
  const touch = model.keys.touch === true;
  const left = slots.filter((s) => s.side === 'left');
  const right = slots.filter((s) => s.side === 'right');
  const sides = model.keys.touch ? { left: 0, right: 0 } : model.keys;
  const rows = touch ? 0 : Math.max(sides.left, sides.right);
  const label = (k: string) => {
    const pk = byKey.get(k);
    return pk ? keyText(pk, nameOf) : '';
  };

  const keyButton = (slot: KeySlot, variant: 'side' | 'tile') => {
    const pk = byKey.get(slot.key);
    const text = label(slot.key);
    const t = pk ? keyType(pk.type) : undefined;
    const what = pk ? (pk.type === 'other' ? 'set in the phone system' : (t?.label ?? pk.type)) : 'phone default';
    return (
      <button
        key={slot.key}
        type="button"
        className={`pk ${variant}${slot.side === 'right' ? ' right' : ''}${selected === slot.key ? ' selected' : ''}${pk ? '' : ' unset'}${changed?.has(slot.key) ? ' changed' : ''}`}
        aria-pressed={onSelect ? selected === slot.key : undefined}
        aria-label={`${keyName(slot.key)}: ${text || 'not set'} (${what})`}
        onClick={onSelect ? () => onSelect(slot.key) : undefined}
        disabled={!onSelect}
      >
        <span className={`pk-led ${ledOf(pk)}`} aria-hidden="true" />
        <span className="pk-label">{text || (editing ? <span className="pk-plus">+</span> : '')}</span>
      </button>
    );
  };

  const nub = (slot: KeySlot | undefined, i: number, side: 'left' | 'right') => (
    <span
      key={`${side}-${i}`}
      className={`pk-nub${slot && selected === slot.key ? ' selected' : ''}${slot ? '' : ' none'}`}
      aria-hidden="true"
      onClick={slot && onSelect ? () => onSelect(slot.key) : undefined}
    />
  );

  const isPageKeyRow = (side: 'left' | 'right', i: number) => pageKey && side === 'right' && i === sides.right - 1;

  const exp = model.expansion;
  const expKeys = exp ? Array.from({ length: exp.perPage }, (_, i) => expPage * exp.perPage + i + 1) : [];

  return (
    <div className="phone-wrap">
      {(pages.length > 1 || (exp && modules > 0)) && (
        <div className="phone-pages" role="group" aria-label="Key pages">
          {pages.map((_, i) => (
            <button key={i} type="button" className={page === i ? 'on' : ''} aria-pressed={page === i} onClick={() => setPage(i)}>
              Page {i + 1}
            </button>
          ))}
        </div>
      )}
      <div className="phone-row">
        <div
          className={`phone-device fam-${model.family}${model.screen.color ? ' color' : ' mono'}${touch ? ' touch' : ''}`}
          aria-label={`${model.vendor} ${model.model}`}
          role="group"
        >
          <div className="phone-handset" aria-hidden="true" />
          <div className="phone-body">
            <div className="phone-face" style={{ '--rows': Math.max(rows, 1) } as CSSProperties}>
              {!touch && sides.left > 0 && <div className="pk-nubs left">{Array.from({ length: rows }, (_, i) => nub(left[i], i, 'left'))}</div>}
              <div className={`phone-screen${touch ? ' touch' : ''}`}>
                <div className="screen-top">
                  <span>{owner ?? ''}</span>
                  <span className="mono">{model.model}</span>
                </div>
                {touch ? (
                  <div className="screen-tiles">{slots.map((s) => keyButton(s, 'tile'))}</div>
                ) : (
                  <div className="screen-keys">
                    <div className="screen-col">
                      {Array.from({ length: sides.left }, (_, i) => (left[i] ? keyButton(left[i]!, 'side') : <span key={i} className="pk ghost" />))}
                    </div>
                    <div className="screen-mid" aria-hidden="true">
                      <span className="screen-clock">9:41</span>
                      <span className="screen-date">{pages.length > 1 ? `Page ${page + 1} of ${pages.length}` : ''}</span>
                    </div>
                    <div className="screen-col right">
                      {Array.from({ length: sides.right }, (_, i) =>
                        right[i] ? (
                          keyButton(right[i]!, 'side')
                        ) : isPageKeyRow('right', i) ? (
                          <button key="page" type="button" className="pk side right page-key" onClick={() => setPage((p) => (p + 1) % pages.length)} aria-label="Next page of keys">
                            <span className="pk-label">
                              Page {page + 1}/{pages.length} ›
                            </span>
                          </button>
                        ) : (
                          <span key={i} className="pk ghost" />
                        )
                      )}
                    </div>
                  </div>
                )}
                <div className="screen-soft" aria-hidden="true">
                  {['History', 'Directory', 'DND', 'Menu'].slice(0, Math.max(0, model.softkeys)).map((s) => (
                    <span key={s}>{s}</span>
                  ))}
                </div>
              </div>
              {!touch && sides.right > 0 && (
                <div className="pk-nubs right">
                  {Array.from({ length: rows }, (_, i) =>
                    isPageKeyRow('right', i) ? (
                      <span key="pg" className="pk-nub page" aria-hidden="true" onClick={() => setPage((p) => (p + 1) % pages.length)} />
                    ) : (
                      nub(right[i], i, 'right')
                    )
                  )}
                </div>
              )}
            </div>
            <div className="phone-keypad" aria-hidden="true">
              <div className="pad-soft">
                {Array.from({ length: 4 }, (_, i) => (
                  <span key={i} />
                ))}
              </div>
              <div className="pad-main">
                <div className="pad-nav">
                  <span className="ring" />
                </div>
                <div className="pad-digits">
                  {['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'].map((d) => (
                    <span key={d}>{d}</span>
                  ))}
                </div>
                <div className="pad-fn">
                  {Array.from({ length: 4 }, (_, i) => (
                    <span key={i} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        {exp &&
          Array.from({ length: modules }, (_, m) => (
            <div key={m} className="phone-exp" role="group" aria-label={`Expansion module ${m + 1} (${exp.name})`}>
              <div className="exp-screen">
                {[0, 1].map((col) => (
                  <div key={col} className="screen-col">
                    {expKeys
                      .slice(col * (exp.perPage / 2), (col + 1) * (exp.perPage / 2))
                      .map((n) => keyButton({ key: `E${m + 1}-${n}`, page: 0, side: col ? 'right' : 'left', row: 0 }, 'side'))}
                  </div>
                ))}
              </div>
              <div className="exp-foot">
                <span className="mono">{exp.name}</span>
                {exp.pages > 1 && (
                  <span className="exp-pages">
                    {Array.from({ length: exp.pages }, (_, i) => (
                      <button
                        key={i}
                        type="button"
                        className={expPage === i ? 'on' : ''}
                        aria-label={`Expansion page ${i + 1}`}
                        aria-pressed={expPage === i}
                        onClick={() => setExpPage(i)}
                      >
                        {i + 1}
                      </button>
                    ))}
                  </span>
                )}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

/** Where a key is, in words: "Page 2, left side, 3rd key". `lineKeys`: as for PhoneLayout. */
export function whereIs(model: PhoneModel, key: string, lineKeys?: number): string {
  const e = /^E(\d)-(\d+)$/.exec(key);
  if (e && model.expansion) {
    const n = Number(e[2]);
    const page = Math.floor((n - 1) / model.expansion.perPage) + 1;
    return `Expansion module ${e[1]}${model.expansion.pages > 1 ? `, page ${page}` : ''}`;
  }
  const { pages } = lineKeySlots(model, lineKeys ?? Math.max(model.lineKeys, Number(/^L(\d+)$/.exec(key)?.[1] ?? 0)));
  for (const slots of pages) {
    const s = slots.find((x) => x.key === key);
    if (!s) continue;
    const where = s.side === 'screen' ? 'on the screen' : `${s.side} of the screen, ${ordinal(s.row + 1)} from the top`;
    return `${pages.length > 1 ? `Page ${s.page}, ` : ''}${where}`;
  }
  return '';
}

function ordinal(n: number): string {
  return `${n}${n % 10 === 1 && n !== 11 ? 'st' : n % 10 === 2 && n !== 12 ? 'nd' : n % 10 === 3 && n !== 13 ? 'rd' : 'th'}`;
}
